"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { RiArrowRightUpLine, RiBookOpenLine, RiMoreFill, RiPlayCircleLine, RiSkipForwardLine } from "react-icons/ri";
import { ProgressBarLink, useProgressNavigation } from "@/components/progress-bar";
import AIBadge from "@/components/ui/ai-badge";
import Badge from "@/components/ui/badge";
import { CheckCircle } from "@/components/ui/check-circle";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { useCanUseAskDecoda } from "@/hooks/use-can-use-ask-decoda";
import { cx, tv } from "@/lib/tailwind-utils";
import { type TrainingSurface, captureTrainingAction, routePath } from "@/lib/training";
import type { TrainingItem } from "@/types/training";
import { useCompleteTrainingItem, useSkipTrainingItem, useUncompleteTrainingItem, useUnskipTrainingItem } from "@/api/provider/training/training-query";
import { askDecodaSidebarDefinition, assistantSidebarDefinition } from "@/features/sidebar/assistant-sidebar";
import { useAskDecodaStore } from "@/stores/ask-decoda-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useTrainingStore } from "@/stores/training-store";
const rowVariants = tv({
  slots: {
    header: "flex items-start gap-2",
    check: "flex shrink-0 items-center",
    // The row's vertical padding belongs to the toggle, so the whole row opens the task.
    toggle: "flex min-w-0 flex-1 items-start gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    title: "min-w-0",
    details: "space-y-2.5 pb-3",
    description: "max-w-prose leading-relaxed text-foreground-secondary",
    guidance: "max-w-prose whitespace-pre-line leading-relaxed text-foreground-secondary",
    actions: "flex min-w-0 flex-wrap items-center gap-y-1.5",
    primary: "inline-flex items-center gap-1 rounded border border-border bg-background font-medium text-foreground hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    textLink: "inline-flex items-center gap-1 rounded-sm font-medium text-foreground underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    more: "inline-flex shrink-0 items-center justify-center rounded border border-border bg-background text-foreground-secondary hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    icon: ""
  },
  variants: {
    size: {
      sm: {
        header: "py-2",
        check: "h-4",
        toggle: "-my-2 py-2",
        title: "text-caption-strong",
        details: "pl-6",
        description: "text-caption",
        guidance: "text-caption",
        actions: "gap-x-2",
        primary: "px-2 py-1 text-caption",
        textLink: "text-caption",
        more: "size-[26px]",
        icon: "size-3"
      },
      md: {
        header: "py-2.5",
        check: "h-5",
        toggle: "-my-2.5 py-2.5",
        title: "text-body-strong",
        details: "pl-7",
        description: "text-body",
        guidance: "text-body",
        actions: "gap-x-2",
        primary: "h-8 px-3 text-body",
        textLink: "h-8 text-body",
        more: "size-8",
        icon: "size-4"
      }
    }
  },
  defaultVariants: {
    size: "sm"
  }
});
const HOW_IT_TICKS: Record<TrainingItem["task"]["completion"], string> = {
  DETECTED: "Ticks itself off once you've done it.",
  TOUR: "Ticks off when you finish the tour.",
  MANUAL: "Tick it off yourself when you're done."
};
export function TrainingTaskRow(t0) {
  const $ = _c(30);
  const {
    item,
    expanded,
    onToggleExpanded,
    size: t1,
    surface,
    onNavigate
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const {
    task
  } = item;
  const styles = rowVariants({
    size
  });
  const complete = useCompleteTrainingItem();
  const uncomplete = useUncompleteTrainingItem();
  const skip = useSkipTrainingItem();
  const unskip = useUnskipTrainingItem();
  const busy = complete.isPending || uncomplete.isPending || skip.isPending || unskip.isPending;
  const isDone = item.status === "DONE";
  const isSkipped = item.status === "SKIPPED";
  const canToggle = !busy && task.completion === "MANUAL" && (!isDone || item.completedVia === "MANUAL");
  const [celebrating, setCelebrating] = useState(false);
  const timer = useRef(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== complete || $[3] !== isDone || $[4] !== item || $[5] !== surface || $[6] !== uncomplete) {
    t4 = () => {
      if (isDone) {
        uncomplete.mutate(item.id);
        captureTrainingAction(item, "uncomplete", surface);
        return;
      }
      setCelebrating(true);
      complete.mutate(item.id);
      captureTrainingAction(item, "complete", surface);
      timer.current = setTimeout(() => setCelebrating(false), 350);
    };
    $[2] = complete;
    $[3] = isDone;
    $[4] = item;
    $[5] = surface;
    $[6] = uncomplete;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const toggleDone = t4;
  const canUseAskDecoda = useCanUseAskDecoda();
  let t5;
  if ($[8] !== item || $[9] !== surface || $[10] !== task.title) {
    t5 = () => {
      captureTrainingAction(item, "ask_decoda", surface);
      const store = useAskDecodaStore.getState();
      store.startNewConversation();
      store.setInitialQuestion(`How do I do this in Decoda: ${task.title}?`);
      useRightSidebarStore.getState().open(assistantSidebarDefinition, {
        targetId: askDecodaSidebarDefinition.id
      });
    };
    $[8] = item;
    $[9] = surface;
    $[10] = task.title;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const askDecoda = t5;
  let t6;
  if ($[12] !== task.route) {
    t6 = routePath(task.route);
    $[12] = task.route;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const path = t6;
  const showMe = task.route !== null && !!task.tour && !!path;
  const hasMore = showMe || !!task.docsUrl || canUseAskDecoda || !isDone;
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const t7 = item.id;
  const t8 = expanded ? "pb-1" : "md:hover:bg-hover";
  let t9;
  if ($[14] !== t8) {
    t9 = cx("-mx-2 rounded-md px-2 transition-colors duration-150 motion-reduce:transition-none", t8);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = canToggle ? toggleDone : undefined;
  const t11 = isDone ? `Reopen ${task.title}` : `Mark ${task.title} done`;
  let t12;
  if ($[16] !== busy || $[17] !== celebrating || $[18] !== isDone || $[19] !== t10 || $[20] !== t11) {
    t12 = <CheckCircle checked={isDone} celebrating={celebrating} onToggle={t10} disabled={busy} label={t11} />;
    $[16] = busy;
    $[17] = celebrating;
    $[18] = isDone;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== isSkipped) {
    t13 = isSkipped && <Badge size="xs" className="mt-px shrink-0">Skipped</Badge>;
    $[22] = isSkipped;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]";
  let t15;
  if ($[24] !== t14) {
    t15 = cx("grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none", t14);
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isDone || $[27] !== isSkipped || $[28] !== task.completion) {
    t16 = !isDone && !isSkipped && <p className="text-caption text-foreground-tertiary">{HOW_IT_TICKS[task.completion]}</p>;
    $[26] = isDone;
    $[27] = isSkipped;
    $[28] = task.completion;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return <li data-training-task={t7} className={t9}><div className={styles.header()}><span className={styles.check()}>{t12}</span><button type="button" onClick={onToggleExpanded} aria-expanded={expanded} className={styles.toggle()}><span className={styles.title({
          className: isDone ? "text-foreground-tertiary line-through" : isSkipped ? "text-foreground-secondary" : "text-foreground"
        })}>{task.title}</span>{t13}</button></div><div className={t15}><div className="min-h-0 overflow-hidden" inert={!expanded} aria-hidden={!expanded}><div className={styles.details()}><p className={styles.description()}>{task.description}</p>{size === "md" && <p className={styles.guidance()}>{task.guidance}</p>}{t16}{isSkipped ? <button type="button" disabled={busy} onClick={() => {
            unskip.mutate(item.id);
            captureTrainingAction(item, "unskip", surface);
          }} className={styles.textLink()}>Unskip</button> : <div className={styles.actions()}>{showMe ? <ProgressBarLink href={task.route} onClick={event => {
              captureTrainingAction(item, "show_me", surface);
              if (event.metaKey || event.ctrlKey) {
                return;
              }
              useTrainingStore.getState().queueTour(task.tour, path);
              onNavigate?.();
            }} className={styles.primary()}><RiPlayCircleLine className={styles.icon()} aria-hidden="true" /><span>Show me</span></ProgressBarLink> : task.route !== null && <ProgressBarLink href={task.route} onClick={() => {
              captureTrainingAction(item, "go_there", surface);
              onNavigate?.();
            }} className={styles.primary()}><span>Go there</span><RiArrowRightUpLine className={styles.icon()} aria-hidden="true" /></ProgressBarLink>}{hasMore && <DropdownMenu><DropdownMenuTrigger asChild={true}><button type="button" aria-label={`More for ${task.title}`} className={styles.more()}><RiMoreFill className={styles.icon()} /></button></DropdownMenuTrigger><DropdownMenuContent align="start">{showMe && <DropdownMenuItem icon={<RiArrowRightUpLine aria-hidden="true" />} onSelect={() => {
                  captureTrainingAction(item, "go_there", surface);
                  navigateWithProgress(task.route);
                  onNavigate?.();
                }}>Go there</DropdownMenuItem>}{task.docsUrl && <DropdownMenuItem icon={<RiBookOpenLine aria-hidden="true" />} onSelect={() => {
                  captureTrainingAction(item, "docs", surface);
                  window.open(task.docsUrl, "_blank", "noopener,noreferrer");
                }}>Guide</DropdownMenuItem>}{canUseAskDecoda && <DropdownMenuItem icon={<AIBadge size={14} className="mr-0" />} onSelect={askDecoda}>Ask Decoda</DropdownMenuItem>}{!isDone && <DropdownMenuItem icon={<RiSkipForwardLine aria-hidden="true" />} disabled={busy} onSelect={() => {
                  skip.mutate(item.id);
                  captureTrainingAction(item, "skip", surface);
                }}>Skip</DropdownMenuItem>}</DropdownMenuContent></DropdownMenu>}</div>}</div></div></div></li>;
}